// Low-level HTTP for the dashboard API.
//
// Auth is a secure httpOnly session cookie set by /auth/login — the browser sends it
// automatically on same-origin requests, so we never touch a token for authentication.
// Mutations (alert ack/resolve) additionally carry a CSRF token via the `X-CSRF-Token`
// header. The token is a NON-httpOnly value the server exposes either as a `wt_csrf` cookie
// (double-submit) or in the /auth/me | /auth/login response body — we accept both.

import type { ApiErrorBody } from '../types/api';

export const API_BASE = '/api/v1';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

let csrfToken: string | null = null;

/** Remember a CSRF token seen in an auth response body. */
export function setCsrfToken(token: string | null | undefined): void {
  if (token) csrfToken = token;
}

export function clearCsrfToken(): void {
  csrfToken = null;
}

/** Read the double-submit cookie if the server uses one. */
function csrfFromCookie(): string | null {
  const m = document.cookie.match(/(?:^|;\s*)wt_csrf=([^;]+)/);
  return m && m[1] ? decodeURIComponent(m[1]) : null;
}

export function currentCsrfToken(): string | null {
  return csrfToken ?? csrfFromCookie();
}

async function parseError(res: Response): Promise<ApiError> {
  let code = 'http_' + res.status;
  let message = res.statusText || 'Request failed';
  try {
    const body = (await res.json()) as Partial<ApiErrorBody>;
    if (body && body.error) {
      code = body.error.code ?? code;
      message = body.error.message ?? message;
    }
  } catch {
    /* non-JSON error body — keep defaults */
  }
  return new ApiError(res.status, code, message);
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  csrf?: boolean;
}

/** Typed JSON request. Throws {@link ApiError} on non-2xx. */
export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json' };
  let body: string | undefined;

  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }
  if (opts.csrf) {
    const token = currentCsrfToken();
    if (token) headers['X-CSRF-Token'] = token;
  }

  const res = await fetch(API_BASE + path, {
    method,
    headers,
    body,
    credentials: 'include',
    signal: opts.signal,
    // Read-only client: never cache stale snapshots.
    cache: 'no-store',
  });

  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;

  const token = res.headers.get('X-CSRF-Token');
  if (token) setCsrfToken(token);

  return (await res.json()) as T;
}

export function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  return request<T>(path, { method: 'GET', signal });
}

export function post<T>(path: string, body?: unknown, opts: { csrf?: boolean; signal?: AbortSignal } = {}): Promise<T> {
  return request<T>(path, { method: 'POST', body, csrf: opts.csrf, signal: opts.signal });
}

export function del<T>(path: string, opts: { csrf?: boolean; signal?: AbortSignal } = {}): Promise<T> {
  return request<T>(path, { method: 'DELETE', csrf: opts.csrf, signal: opts.signal });
}
